import { Observable, Subject, timer } from 'rxjs';

import { CustomerDetails, DeviceResource, UserDetails } from './check-in.interfaces';
import { CheckInEffects, getCheckInStatus, isCustomerValid } from './check-in.state';

import { environment } from '../../../environments/environment';
import { setCustomerSettings } from '../customer/customer.state';
import { setDeviceResource } from '../device/device.state';
import { rootStore } from '../extension-store/extension.store';
import { StateStatus } from '../extension-store/interfaces/extension-store.interfaces';
import { setLicenseStatus } from '../licensing/licensing.state';
import { setPublicIp } from '../network-status/network-status.state';
import { setProfileDetails } from '../user-profile/user-profile.state';
import UtilsSerice from '../utils/utils.service';

const checkInTimer: Observable<number> = timer(0, environment.defaultTimings.checkInIntervalInSeconds * 1000);
const forceCheckinTrigger: Subject<void> = new Subject<void>();

let retries = 0;
let retryBackoffInSeconds = 5;

const retryCheckIn = (): void => {
  timer(retryBackoffInSeconds * 1000).subscribe(() => {
    UtilsSerice.debugLog(
      `CheckInService CheckforRetry`,
      `Retrying checkin attempt ${retries}`,
      `Retrying in ${retryBackoffInSeconds} seconds`
    );
    retries++;
    retryBackoffInSeconds = retryBackoffInSeconds < 60 ? (retryBackoffInSeconds *= 2) : 60;
    forceCheckinTrigger.next();
  });
};

const checkforRetry = (): boolean => {
  const MAX_RETRIES = 15;
  const newAppState = rootStore.getState();
  if (retries >= MAX_RETRIES) {
    UtilsSerice.debugLog(`CheckInService Max retries reached`);
  }
  return (
    (getCheckInStatus(newAppState) === StateStatus.FAILED || !isCustomerValid(newAppState)) && retries < MAX_RETRIES
  );
};

const saveDeviceDetails = (deviceDetails?: DeviceResource): void => {
  if (!deviceDetails) return;
  rootStore.dispatch(setPublicIp(deviceDetails?.publicIP ?? 'NOTE_SET'));
  rootStore.dispatch(setDeviceResource(deviceDetails));
};

const saveCustomerSettings = (customerDetails?: CustomerDetails): void => {
  if (!customerDetails) return;
  rootStore.dispatch(setCustomerSettings(customerDetails));
};

const saveUserDetails = (userDetails?: UserDetails): void => {
  if (!userDetails) return;
  rootStore.dispatch(setProfileDetails(userDetails));
};

const saveLicenseState = (isLicensed?: boolean): void => {
  rootStore.dispatch(setLicenseStatus(isLicensed ?? false));
};

const CheckInService = {
  init: async () => {
    forceCheckinTrigger.subscribe(async (): Promise<void> => {
      UtilsSerice.debugLog(`CheckInService Forced Triggered`);
      await rootStore.dispatch(CheckInEffects.checkIn());
      if (checkforRetry()) {
        retryCheckIn();
      }
    });

    checkInTimer.subscribe(async () => {
      UtilsSerice.debugLog(`CheckInService Time Triggered`);
      await rootStore.dispatch(CheckInEffects.checkIn());
      if (checkforRetry()) {
        retryCheckIn();
      }
      const newAppState = rootStore.getState();
      saveDeviceDetails(newAppState.checkInState.extensionCheckinResponse?.deviceDetails);
      saveCustomerSettings(newAppState.checkInState.extensionCheckinResponse?.customerDetails);
      saveUserDetails(newAppState.checkInState.extensionCheckinResponse?.userDetails);
      saveLicenseState(newAppState.checkInState.extensionCheckinResponse?.isLicensed);
    });
  },
};

export default CheckInService;
